/**
 * Functioning Assessment and Goals types for VisitProof.
 *
 * Assessments are stored as subcollections: cases/{caseId}/assessments/{assessmentId}
 * Goals are stored as subcollections: cases/{caseId}/goals/{goalId}
 */

import { BaseEntity, DateField } from './base';

// ============================================================================
// FUNCTIONING ASSESSMENT
// ============================================================================

/**
 * Categories of functioning being assessed.
 */
export type AssessmentCategory =
  | "parent_child_relationship"
  | "child_functioning"
  | "parent_functioning";

export const ASSESSMENT_CATEGORY_LABELS: Record<AssessmentCategory, string> = {
  parent_child_relationship: "Parent-Child Relationship",
  child_functioning: "Child Functioning",
  parent_functioning: "Parent Functioning",
};

/**
 * Individual rating dimensions within each category.
 */
export type AssessmentDimension =
  // Parent-child relationship
  | "connection"
  | "trust"
  | "communication"
  // Child functioning
  | "emotional_regulation"
  | "behavior"
  | "comfort_level"
  // Parent functioning
  | "responsiveness"
  | "parent_emotional_regulation"
  | "parenting_confidence";

export const ASSESSMENT_DIMENSION_LABELS: Record<AssessmentDimension, string> = {
  // Parent-child relationship
  connection: "Connection",
  trust: "Trust",
  communication: "Communication",
  // Child functioning
  emotional_regulation: "Emotional Regulation",
  behavior: "Behavior",
  comfort_level: "Comfort Level",
  // Parent functioning
  responsiveness: "Responsiveness",
  parent_emotional_regulation: "Emotional Regulation",
  parenting_confidence: "Parenting Confidence",
};

/**
 * Maps each category to its dimensions.
 */
export const CATEGORY_DIMENSIONS: Record<AssessmentCategory, AssessmentDimension[]> = {
  parent_child_relationship: ["connection", "trust", "communication"],
  child_functioning: ["emotional_regulation", "behavior", "comfort_level"],
  parent_functioning: ["responsiveness", "parent_emotional_regulation", "parenting_confidence"],
};

/**
 * Whether an assessment is a baseline (initial) or follow-up measurement.
 */
export type AssessmentType = "baseline" | "follow_up";

export const ASSESSMENT_TYPE_LABELS: Record<AssessmentType, string> = {
  baseline: "Baseline",
  follow_up: "Follow-Up",
};

/**
 * A single rating for a dimension (1-5 scale).
 */
export interface DimensionRating {
  dimension: AssessmentDimension;
  rating: number; // 1-5
}

/**
 * A functioning assessment for a case.
 * Stored as: cases/{caseId}/assessments/{assessmentId}
 */
export interface FunctioningAssessment extends BaseEntity {
  caseId: string;
  accountId: string;
  /** Baseline or follow-up */
  type: AssessmentType;
  /** Date the assessment was conducted */
  assessmentDate: DateField;
  /** Individual dimension ratings */
  ratings: DimensionRating[];
  /** Optional notes about the assessment */
  notes?: string;
  /** Who conducted the assessment */
  assessedBy: string;
  /** Display name of assessor (denormalized) */
  assessedByName?: string;
}

// ============================================================================
// GOALS
// ============================================================================

export type GoalStatus = "active" | "completed" | "discontinued";

export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  active: "Active",
  completed: "Completed",
  discontinued: "Discontinued",
};

export type GoalCategory =
  | "parenting_skills"
  | "communication"
  | "safety"
  | "emotional_wellbeing"
  | "reunification"
  | "compliance"
  | "other";

export const GOAL_CATEGORY_LABELS: Record<GoalCategory, string> = {
  parenting_skills: "Parenting Skills",
  communication: "Communication",
  safety: "Safety",
  emotional_wellbeing: "Emotional Wellbeing",
  reunification: "Reunification",
  compliance: "Compliance",
  other: "Other",
};

/**
 * A goal for a case.
 * Stored as: cases/{caseId}/goals/{goalId}
 */
export interface CaseGoal extends BaseEntity {
  caseId: string;
  accountId: string;
  /** Goal title */
  title: string;
  /** Detailed description */
  description?: string;
  /** Goal category */
  category?: GoalCategory;
  /** Current status */
  status: GoalStatus;
  /** Target date for completion */
  targetDate?: DateField;
  /** Date goal was completed or discontinued */
  resolvedDate?: DateField;
  /** Notes about progress or resolution */
  progressNotes?: string;
  /** Who created the goal */
  createdByUserId: string;
  /** Display name (denormalized) */
  createdByName?: string;
}
